import type { ReactNode } from 'react';
import { create } from 'zustand';
import type { AlertProps } from '@mui/material';


export interface WebDialogState {
  show: boolean;
  url: string;
  label: string;
}

export interface OrgSetupWizardState {
  open: boolean;
  organizationId: number | null;
  createError: string | null;
}

export interface AppLoadingState {
  isLoading: boolean;
  label?: string;
}

export interface AppSnackbarState {
  autoHideDuration: number | null;
  open: boolean;
  message: ReactNode;
  severity: AlertProps['severity'];
  caller?: string;
}

const MASK_MODE_KEY = '__HARMONI_MASK_MODE';

export const INITIAL_WEB_DIALOG: WebDialogState = {
  show: false,
  url: '',
  label: '',
};

export const INITIAL_ORG_SETUP_WIZARD: OrgSetupWizardState = {
  open: false,
  organizationId: null,
  createError: null,
};

export const INITIAL_LOADING: AppLoadingState = {
  isLoading: false,
  label: '',
};

export const INITIAL_SNACKBAR: AppSnackbarState = {
  autoHideDuration: null,
  open: false,
  message: '',
  severity: undefined,
};

function readInitialMaskMode(): boolean {
  try {
    return localStorage.getItem(MASK_MODE_KEY) === 'true';
  } catch {
    return false;
  }
}

function readInitialDashboardCarouselMode(): boolean {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('dashboardCarouselMode') === 'true';
  } catch {
    return false;
  }
}

interface UiStoreState {
  webDialog: WebDialogState;
  orgSetupWizard: OrgSetupWizardState;
  loading: AppLoadingState;
  snackbar: AppSnackbarState;
  maskMode: boolean;
  dashboardCarouselMode: boolean;

  setWebDialog: (webDialog: WebDialogState) => void;
  closeWebDialog: () => void;
  openOrgSetupWizard: (organizationId?: number | null) => void;
  closeOrgSetupWizard: () => void;
  setOrgSetupCreateError: (createError: string | null) => void;
  setLoading: (loading: AppLoadingState) => void;
  setSnackbar: (snackbar: AppSnackbarState) => void;
  setMaskMode: (maskMode: boolean) => void;
  setDashboardCarouselMode: (dashboardCarouselMode: boolean) => void;
  resetUiOnLogout: () => void;
}

export const useUiStore = create<UiStoreState>((set) => ({
  webDialog: { ...INITIAL_WEB_DIALOG },
  orgSetupWizard: { ...INITIAL_ORG_SETUP_WIZARD },
  loading: { ...INITIAL_LOADING },
  snackbar: { ...INITIAL_SNACKBAR },
  maskMode: readInitialMaskMode(),
  dashboardCarouselMode: readInitialDashboardCarouselMode(),

  setWebDialog: (webDialog) => set({ webDialog }),
  closeWebDialog: () => set({ webDialog: { ...INITIAL_WEB_DIALOG } }),
  openOrgSetupWizard: (organizationId = null) =>
    set({
      orgSetupWizard: {
        open: true,
        organizationId: organizationId ?? null,
        createError: null,
      },
    }),
  closeOrgSetupWizard: () =>
    set({ orgSetupWizard: { ...INITIAL_ORG_SETUP_WIZARD } }),
  setOrgSetupCreateError: (createError) =>
    set((state) => ({
      orgSetupWizard: { ...state.orgSetupWizard, createError: createError || null },
    })),
  setLoading: (loading) => set({ loading }),
  setSnackbar: (snackbar) => set({ snackbar }),
  setMaskMode: (maskMode) => {
    try {
      localStorage.setItem(MASK_MODE_KEY, String(maskMode));
    } catch {
      // ignore storage errors — in-memory state stays the source of truth
    }
    set({ maskMode });
  },
  setDashboardCarouselMode: (dashboardCarouselMode) =>
    set({ dashboardCarouselMode }),
  resetUiOnLogout: () =>
    set({
      snackbar: { ...INITIAL_SNACKBAR },
      webDialog: { ...INITIAL_WEB_DIALOG },
      orgSetupWizard: { ...INITIAL_ORG_SETUP_WIZARD },
      maskMode: false,
      dashboardCarouselMode: false,
    }),
}));

export const showSnackbar = (snackbar: Partial<AppSnackbarState>) =>
  useUiStore.getState().setSnackbar({ ...INITIAL_SNACKBAR, ...snackbar });
export const setAppLoading = (loading: AppLoadingState) =>
  useUiStore.getState().setLoading(loading);
export const setWebDialog = (webDialog: WebDialogState) =>
  useUiStore.getState().setWebDialog(webDialog);
export const closeWebDialog = () => useUiStore.getState().closeWebDialog();
export const openOrgSetupWizard = (organizationId?: number | null) =>
  useUiStore.getState().openOrgSetupWizard(organizationId);
export const closeOrgSetupWizard = () =>
  useUiStore.getState().closeOrgSetupWizard();
export const setOrgSetupCreateError = (createError: string | null) =>
  useUiStore.getState().setOrgSetupCreateError(createError);
export const setMaskMode = (maskMode: boolean) =>
  useUiStore.getState().setMaskMode(maskMode);
export const setDashboardCarouselMode = (dashboardCarouselMode: boolean) =>
  useUiStore.getState().setDashboardCarouselMode(dashboardCarouselMode);
export const resetUiOnLogout = () => useUiStore.getState().resetUiOnLogout();

export const useWebDialog = () => useUiStore((s) => s.webDialog);
export const useOrgSetupWizard = () => useUiStore((s) => s.orgSetupWizard);
export const useAppLoading = () => useUiStore((s) => s.loading);
export const useAppSnackbar = () => useUiStore((s) => s.snackbar);
export const useMaskMode = () => useUiStore((s) => s.maskMode);
export const useDashboardCarouselMode = () =>
  useUiStore((s) => s.dashboardCarouselMode);
